/**
 * Table component module.
 * @module shared/components/Table
 *
 */
// ETODO subsequent passes:
//  Re-evaluate use of hooks/memoization
//  (Maybe) split into separate files
//  (Maybe) support client-side filtering, improve client-side sorting (if needed)
//  (Maybe) make so always isControlled?

import {MAX_PAGINATED_ITEMS} from '../../config.json';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useAsyncDebounce, usePagination, useSortBy, useTable} from 'react-table';
import styled, {css, withTheme} from 'styled-components/macro';
import {Button, Checkbox, Column, DescriptiveText, LoadingSpinner, Panel, RadioOption, Row} from '../../shared';
import Search from './Search/Search';
import {fill} from './cssUtils';
import {formatter} from '../utils/utils';
import Icon from './Icon';
import SectionTitle from './SectionTitle';

const _Wrapper = styled.div`
  min-height: 100px;
  height: ${props => props.height || 'fit-content'};
  max-height: ${props => props.maxHeight || '85vh'};
  overflow: hidden;
  ${fill(null)};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  margin: 5px;
`;

const _TableFixHead = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  //noinspection CssInvalidPropertyValue
  // height: -moz-fit-content; // uncommenting this causes header not to stay at top on tables.
  // height: fit-content; // uncommenting this causes header not to stay at top on tables.

  & table {
    ${fill(null)};
    margin-top: -2px;
  }

  & thead th {
    position: sticky;
    top: 0;
  }
`;

const _TableWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  max-width: 100%;
  max-height: 100%;
  overflow-y: auto;
`;

const _Table = styled.table`
  ${props => props.pointerEvents && 'cursor: default !important' || ''};
  pointer-events: ${props => props.pointerEvents || 'auto'};
`;

const _Overlay = styled.div`
  ${fill(null)};
  height: 100%;
  position: absolute;
  background: rgba(0, 0, 0, 0.65);
  top: 0;
  left: 0;
  padding: 5px;
  box-sizing: border-box;
`;

// TODO PENDING REQS Table Column Headers: correct transparency issue on scroll
const _TableHeader = styled.th`
  text-transform: uppercase;
  font-weight: normal;
  height: 30px;
  min-width: ${props => props.column.minWidth}px;
  ${props => props.column.maxWidth && `max-width: ${props.column.maxWidth}px`};
  font-family: ${props => props.theme.titleText.name};
  font-size: 12px;
  color: ${props => props.theme.dark ? 'rgba(255,255,255, 0.9)' : 'rgba(0,0,0,0.9)'};
  text-align: ${props => props.column.alignment};
  background-color: ${props => props.theme.dark ? 'rgba(255,255,255,.2)' : 'rgba(0,0,0,.12)'};
  backdrop-filter: blur(20px);
  border-radius: 8px 8px 0 0;
  padding: 4px;
  ${props => {
    if (props.theme.neumorphic) {
      return css`
        box-shadow: inset 1px 1px 0 0 rgba(255, 255, 255, 0.1), inset -1px -1px 0 0 rgba(0, 0, 0, 0.3), 0 2px 2px 0 rgba(0, 0, 0, 0.2);
        background-image: linear-gradient(to bottom right, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2));
        background-color: ${props.theme.tileBackgroundColor};
      `;
    }
    return css`
      background-color: ${props.theme.dark ? 'rgba(255,255,255,.2)' : 'rgba(0,0,0,.12)'};
    `;
  }};
`;

const _TableRow = styled.tr`
    & td {
    ${props => (!props.theme.neumorphic)
            && `background-color: rgba(${[
              props.theme.dark ? '255,255,255' : '0,0,0',
              props.isActive ? 0.2 : (props.theme.dark && 0.1 || 0.05),
            ].join(',')})`};
    ${props => (props.theme.neumorphic)
            && `background-color: ${props.theme.tileBackgroundColor}`};
    ${props => (props.theme.neumorphic && props.isActive)
            && 'background-image: linear-gradient(to bottom right, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2))'};
    }

  ${props => (props.hasClickAction) && `cursor: ${props.disabled ? 'not-allowed' : 'pointer'};`}

  ${props => (props.hasClickAction && !props.disabled && !props.isActive && !props.theme.neumorphic)
          && `&:hover td {background-color: rgba(${[props.theme.dark ? '255,255,255' : '0,0,0', 0.15].join(',')});}`}
  ${props => (props.hasClickAction && !props.disabled && !props.isActive && props.theme.neumorphic)
          && '&:hover td {background-image: linear-gradient(to bottom right, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.15))}'}

`;

// We may eventually want to separate out Row and Cell styling
// Note this says max width for the input but the way to do that with a table is set the width and let it shrink if needed, there isn't a supported max width.
const _TableCell = styled.td`
  height: 20px;
  min-width: ${props => props.column.minWidth}px;
  ${props => props.column.maxWidth && `max-width: ${props.column.maxWidth}px`};
  font-family: ${props => props.theme.bodyText.name};
  font-size: ${props => props.theme.bodyText.size};
  color: ${props => `rgba(${[props.theme.dark ? '255,255,255' : '0,0,0', props.disabled ? 0.5 : 0.9].join(',')})`};
  text-align: ${props => props.column.alignment};
  border-radius: 3px;
  padding: 4px;
  ${(props) => props.column.verticalAlign ? 'display: flex;flex-direction: row;align-items: center;' : ''};
  ${props => {
    if (props.theme.neumorphic) {
      return css`
        box-shadow: inset 1px 1px 0 0 rgba(255, 255, 255, 0.05), inset -1px -1px 0 0 rgba(0, 0, 0, 0.2);
        background-image: linear-gradient(to bottom right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1));
      `;
    }
  }};

  ${props => {
    if (props.column.wrapBreakWords) {
      return css`
        word-break: break-word;
        hyphens: auto;
      `;
    }
  }};
`;

const _PaginationControls = styled.div`
  height: 30px;
  box-sizing: border-box;
  min-width: 100px;
  padding: 5px;
  margin: 5px 0 5px 5px;
  border-radius: 15px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  ${props => {
    if (props.theme.neumorphic) {
      return css`
        box-shadow: inset 1px 1px 0 0 rgba(255, 255, 255, 0.1), inset -1px -1px 0 0 rgba(0, 0, 0, 0.5);
        background-image: linear-gradient(to bottom right, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05));
        background-color: ${props.theme.tileBackgroundColor};
      `;
    }
    return css`
      background-color: ${props.theme.dark ? 'rgba(255,255,255,.05)' : 'rgba(0,0,0,.05)'};
    `;
  }};

`;

const _PageCount = styled.span`
  width: fit-content;
  font-family: ${props => props.theme.labelText.name};
  font-size: 10px;
  color: ${props => props.theme.dark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'};
`;

const _SelectAllMessage = styled.span`
  width: fit-content;
  font-family: ${props => props.theme.bodyText.name};
  color: ${props => props.theme.dark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'};
`;

const _PanelSelector = styled.div`
  width: 40px;
  //flex: 0 0 50px;
  padding: 5px;
  margin: 0 0 0 5px;
  border-radius: 3px;
  ${props => {
    if (props.theme.neumorphic) {
      return css`
        box-shadow: inset 1px 1px 0 0 rgba(255, 255, 255, 0.1), inset -1px -1px 0 0 rgba(0, 0, 0, 0.3), 0 2px 2px 0 rgba(0, 0, 0, 0.2);
        background-image: linear-gradient(to bottom right, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2));
        background-color: ${props.theme.tileBackgroundColor};
      `;
    }
    return css`
      background-color: ${props.theme.dark ? 'rgba(255,255,255,.05)' : 'rgba(0,0,0,.05)'};
    `;
  }};

`;

const _IconTitle = styled.div`
  color: ${props => props.color};
`;

/**
 * Table component based on 'react-table' to build table from passed props.
 * @name Table
 */
const Table = props => {
  const {
    dataSet = [],
    dataManager,
    columns,
    select,
    defaultSort,
    rowsPerPage = MAX_PAGINATED_ITEMS,
    height,
    maxHeight,
    rowIdentifier,
    rowOnClick,
    rowOnClickDisabled,
    searches,
    defaultSearch,
    filters,
    filterValues,
    iconsKey,
    primaryColumn,
    infoPanel,
    subContent,
    session,
    noData = {},
  } = props;
  const {
    standard: noDataMsg = 'No data could be found.',
    filtered: noFilteredDataMsg = 'No results found matching search criteria.',
  } = noData;

  const isControlled = !!(dataManager && dataManager.gatherData); // ETODO reconsider this. consolidate eventually? gatherData client-side?
  const hasPreselectedRows = !!(select && select.preselectedRows && select.preselectedRows.length);
  const [error, setError] = useState();
  const fetchIdRef = useRef(0);

  // TODO Requirements for standardized data formatting in tables
  const columnDefs = useMemo(
    () => columns.map((col, idx) => {
      let alignment;
      let columnFormatter;
      switch (col.type) {
        case 'text':
          alignment = 'left';
          if (col.charLimit) {
            columnFormatter = value => {
              if (typeof value === 'string') {
                const shortStr = value.substr(0, col.charLimit);
                if (shortStr.length === value.length) {
                  return value;
                }
                return shortStr.trim() + '...';
              }
              return value || '-';
            };
          }
          break;
        case 'number':
          alignment = 'right';
          break;
        case 'phone':
          alignment = 'left'; // TODO REQS phone numbers in tables aligned how
          columnFormatter = formatter('phone');
          break;
        case 'percent':
          alignment = 'right';
          if (col.decimalPlaces === 0) {
            columnFormatter = formatter('percent', {decimalPlaces: 0});
          } else {
            columnFormatter = formatter('percent', {decimalPlaces: col.decimalPlaces || 1});
          }
          break;
        case 'percentDecimal':
          alignment = 'right';
          if (col.decimalPlaces === 0) {
            columnFormatter = formatter('percentDecimal', {decimalPlaces: 0});
          } else {
            columnFormatter = formatter('percentDecimal', {decimalPlaces: col.decimalPlaces || 1});
          }
          break;
        case 'currency':
          alignment = 'right';
          columnFormatter = formatter('currency');
          break;
        case 'date':
          alignment = 'right';
          columnFormatter = formatter('date');
          break;
        case 'datetime':
          alignment = 'right';
          columnFormatter = formatter('datetime');
          break;
        case 'custom':
          alignment = 'center';
          break;
        case 'object':
          alignment = 'left';
          columnFormatter = formatter('object');
          break;
        default:
      }

      const render = [
        (col.render ? instance => col.render(instance.row.original, instance) : null)
        || (columnFormatter
          ? instance => (instance.value == null || instance.value === '') && '-' || columnFormatter(instance.value)
          : null)
        || (({value}) => String((value == null || value === '') ? '-' : value)
          || (() => '-')),
      ];

      // ETODO PENDING REQS this could share code with icons key
      if (iconsKey && ((primaryColumn && col.dataPoint === primaryColumn) || (!primaryColumn && idx === 0))) {
        const icons = iconsKey.filter(icon => icon.displayCondition);
        if (icons.length) {
          render.push(instance => <Row wrap={false} width={'fit-content'} key={`icons-${col.title}`}>{
            icons.map(icon => icon.displayCondition(instance.row.original, instance)
              ? <Icon key={icon.title}
                      description={typeof icon.description === 'function'
                        ? icon.description(instance.row.original, instance)
                        : icon.description}
                      color={icon.color}
                      width={'20px'}
                      height={'20px'}
                      margin={'5px'}
                      svg={icon.image}
                      action={icon.action && (() => icon.action(instance.row.original))}/>
              : null)
          }</Row>);
        }
      }

      return ({
        // React-Table
        id: col.id,
        Header: col.header || col.title,
        accessor: col.dataPoint,
        Cell: function renderCell(instance) {
          return render.length > 1
            ? <Row wrap={false} justifySubRow={'space between'} alignItems={'middle'}>{render.map(r => r(instance))}</Row>
            : render[0](instance);
        },
        // sortType: // ETODO PENDING REQS May be needed for for non-controlled? Built-in options: basic, datetime, alphanumeric. Defaults to alphanumeric
        disableSortBy: !(subContent && subContent.rowID) ? !col.isSortable : true,

        // Custom
        alignment: col.alignment || alignment,
        minWidth: col.minWidth,
        maxWidth: col.maxWidth,
        width: col.width || 'auto',
        onClick: !(subContent && subContent.rowID) ? col.onClick : null,
        type: col.type,
        hideInSubContent: col.hideInSubContent,
        wrapBreakWords: col.wrapBreakWords !== undefined ? col.wrapBreakWords : (col.type === 'text' || col.type === 'custom'),
        verticalAlign: col.verticalAlign,
      });
    }),
    [columns, select, iconsKey, subContent] // Note Select is here otherwise if columns are memoized changing actions doesn't allow change of checkbox col
  );

  let getRowID;

  if (rowIdentifier) {
    if (typeof rowIdentifier === 'string') {
      getRowID = row => row && row[rowIdentifier];
    } else if (typeof rowIdentifier === 'function') {
      getRowID = rowIdentifier;
    }
  }

  if (select && select.max === 1) {
    select.type = undefined;
  }

  // ETODO PENDING REQS make work for client side?
  const getSearch = (optionName, value) => {
    if (value) {
      const searchOption = searches.find(option => option.name === optionName);
      const valueFormatted = searchOption.isFuzzy ? `~/${value}/` : value;

      return {[optionName]: valueFormatted};
    }
    return {};
  };

  // Hooks - General
  const [data, setData] = useState(dataSet);
  const [loading, setLoading] = useState();

  // Hooks - Display
  const [displayFilters, setDisplayFilters] = useState(false);
  const [rightPanel, setRightPanel] = useState(null);

  // Hooks - Pagination
  const [controlledPageCount, setControlledPageCount] = useState(1);
  const [controlledPageIndex, setControlledPageIndex] = useState(0);
  const [changingPage, setChangingPage] = useState(true);
  const [pageCounter, setPageCounter] = useState({start: 0, end: 0});

  // Hooks - Other
  const [searchFilter, setSearchFilter] = useState(defaultSearch ? getSearch(defaultSearch.option, defaultSearch.value) : {});
  const [selectedRow, setSelectedRow] = useState(hasPreselectedRows ? select.preselectedRows[0] : {}); // Max : 1
  const [selectedRows, setSelectedRows] = useState(hasPreselectedRows ? select.preselectedRows : []); // Max: >1
  const [selectedRowIds, setSelectedRowIds] = useState(hasPreselectedRows ? select.preselectedRows.map(row => getRowID(row)) : []);
  const [allPageRowsSelected, setAllPageRowsSelected] = useState(false);
  const tableRef = useRef(null);

  const resetScroll = () => {
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  };

  const toggleDisplayRightPanel = key => display => {
    if (display !== undefined) {
      setRightPanel(!!display ? key : null);
    } else {
      setRightPanel(rightPanel === key ? null : key);
    }
  };

  const toggleDisplayInfoPanel = toggleDisplayRightPanel('info');
  const toggleDisplaySelectPanel = toggleDisplayRightPanel('select');
  const toggleDisplayIconsPanel = toggleDisplayRightPanel('icons');
  //TODO bug discovered, after deactivating account the account stays inside the selection panel
  let toggleRowSelectedInstance = () => {};
  let toggleAllPageRowsSelectedInstance = () => {};

  const isControlledOptions = {
    autoResetSelectedRows: false,
    manualFilters: true,
    manualPagination: true,
    manualSortBy: true,
    pageCount: controlledPageCount,
    useControlledState: state => useMemo(
      () => ({
        ...state,
        pageIndex: controlledPageIndex,
        selectedRow: selectedRow,
        selectedRows: selectedRows,
        selectedRowIds: selectedRowIds,
        allPageRowsSelected: allPageRowsSelected,
      }),
      [state, controlledPageIndex, selectedRow, selectedRows, selectedRowIds, allPageRowsSelected] // ETODO warning that controlledPageIndex is unnecessary here, but removing breaks
    ),
  };

  const isNotControlledOptions = {
    useControlledState: state => useMemo(
      () => ({
        ...state,
        selectedRow: selectedRow,
        selectedRows: selectedRows,
        selectedRowIds: selectedRowIds,
        allPageRowsSelected: allPageRowsSelected,
      }),
      [state, controlledPageIndex, selectedRow, selectedRows, selectedRowIds, allPageRowsSelected] // ETODO warning that controlledPageIndex is unnecessary here, but removing breaks
    ),
  };

  const tableOptions = {
    columns: columnDefs,
    data: data,
    initialState: { // Pass hoisted table state
      pageIndex: 0,
      pageSize: useMemo(() => rowsPerPage, [rowsPerPage]),
      sortBy: useMemo(() => [
        {
          id: defaultSort.column,
          desc: defaultSort.desc,
        },
      ], [defaultSort]),
    },
    disableMultiSort: true,
    disableSortRemove: true,
    // autoResetPage: false,
    autoResetFilters: false,
    autoResetSortBy: false,
    getRowId: useCallback(getRowID, []),
    ...(isControlled ? isControlledOptions : isNotControlledOptions),
  };

  const mainTable = useTable(
    tableOptions,
    useSortBy,
    usePagination,
    useCallback(hooks => {
      if (select && (!subContent || !subContent.rowID)) {
        hooks.visibleColumns.push(cols => [
          {
            id: 'selection',
            alignment: 'center',
            minWidth: 30,
            width: 30,
            maxWidth: 30,
            Header: function buildHeader(instance) {
              const dataPoint = {
                name: 'selected',
                value: instance.state.allPageRowsSelected,
              };

              const onChange = (name, value) => {
                toggleAllPageRowsSelectedInstance(
                  value === 1,
                  instance.state.selectedRows,
                  instance.state.selectedRowIds,
                  instance.page
                );

                setAllPageRowsSelected(value === 1);
              };
              // TODO Consider disabling the checkbox when there are no results or maybe even no selectable results? Need to discuss this with Jeremy before any code changes are done.

              return !!select.type && <Checkbox
                width={'20px'}
                margin={'0'}
                dataPoint={dataPoint}
                onChange={onChange}
              />;
            },
            Cell: function buildCell(instance) {
              const disabled = select.rowDisabledFunc && select.rowDisabledFunc(instance.row.original);
              let checked = false;

              if (!disabled) {
                if (instance.state.selectedRows === 'ALL') {
                  checked = true;
                } else if (select.max === 1) {
                  checked = instance.state.selectedRow
                    && (getRowID(instance.row.original) === getRowID(instance.state.selectedRow));
                } else {
                  checked = instance.state.selectedRowIds.includes(getRowID(instance.row.original));
                }
              }

              const dataPoint = {
                name: 'selected',
                value: checked,
              };

              const onChange = () => {
                toggleRowSelectedInstance(instance.row.original, instance.state.selectedRows, instance.page);
              };

              const radio = (
                <RadioOption
                  dataPoint={dataPoint}
                  selected={checked}
                  disabled={disabled}
                  onChange={onChange}
                  width={'13px'}
                />
              );

              const checkbox = <Checkbox
                width={'20px'}
                margin={'0'}
                disabled={disabled}
                dataPoint={dataPoint}
                onChange={onChange}
              />;

              return <div>{select.max === 1 ? radio : checkbox}</div>;
            },
          },
          ...cols,
        ]);
      }
    }, [getRowID, selectedRows, selectedRowIds, setSelectedRows, setSelectedRowIds])
  );

  const drilldownOptions = {
    columns: columnDefs,
    data: subContent ? data.filter(row => (getRowID(row) === subContent.rowID)) : data,
  };

  const drilldownTable = useTable(drilldownOptions);

  const {
    rows,
    getTableProps,
    getTableBodyProps,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    state: {pageIndex, pageSize, sortBy}, // Get the state from the react-table instance
  } = (subContent && subContent.rowID) ? drilldownTable : mainTable;

  const {headerGroups} = mainTable;

  const toggleRowSelected = (slt, toggledRow, selRows, pg) => {
    if (slt.rowDisabledFunc && slt.rowDisabledFunc(toggledRow)) {return;}

    const selectablePageRows = slt.rowDisabledFunc
      ? pg.map(row => row.original).filter(row => !slt.rowDisabledFunc(row))
      : pg.map(row => row.original);

    const id = getRowID(toggledRow);
    let index = null;
    const selection = (selRows === 'ALL') ? selectablePageRows : [...selRows];

    const currentRow = selection.find((row, idx) => {
      index = idx;
      return (getRowID(row) === id);
    });

    if (slt.max === 1) {
      setSelectedRow(toggledRow);
    } else {
      if (currentRow) {
        selection.splice(index, 1);
      } else {
        selection.push(toggledRow);
      }

      setSelectedRows(selection);
    }
  };

  /* Callbacks */

  const handleCellClick = useCallback(cell => {
    if (cell.column.id === 'selection') {
      return null;
    }
    const handler = cell.column.onClick || (!cell.row.onClickDisabled && rowOnClick);
    if (handler) {
      handler(cell.row.original, cell.column);
    }
  }, [rowOnClick, subContent]);

  const gotoNextPage = useCallback(() => {
    if (isControlled) {
      setControlledPageIndex(controlledPageIndex + 1);
      setChangingPage(true);
    } else {
      nextPage();
    }
  }, [isControlled, nextPage, controlledPageIndex]);

  const gotoPreviousPage = useCallback(() => {
    if (isControlled) {
      setControlledPageIndex(controlledPageIndex - 1);
      setChangingPage(true);
    } else {
      previousPage();
    }
  }, [isControlled, previousPage, controlledPageIndex]);

  const fetchData = useCallback((pagination, options) => {
    const fetchId = ++fetchIdRef.current;
    const {index, size, sort, search} = pagination;
    const opts = options || {};
    const params = {
      ...search,
      limit: size + 1,
      offset: size * index,
    };

    if (sort && sort.length) {
      params.sort = sort.map(s => (s.desc ? '-' : '+') + s.id)
        .join(',');
    }

    setLoading(true);
    setError(false);
    return dataManager.gatherData(params)
      .then(dataRows => {
        if (fetchId === fetchIdRef.current) {
          if (dataRows.length > size) {
            dataRows.pop();
            if (!opts.skipIncrement) {
              setControlledPageCount(current => current + 1);
            }
          }
          setData(dataRows || []);
          setLoading(false);
          resetScroll();
        }
      })
      .catch(err => {
        setLoading(false);
        setError(true);
        setData([]);
        throw err;
      });
  }, [dataManager]);

  const fetchDataDebounced = useAsyncDebounce(fetchData, 100);

  const handleRefresh = useCallback(() => {
    setControlledPageCount(1);
    setControlledPageIndex(0);

    if (selectedRows === 'ALL') {
      setAllPageRowsSelected(false);
      setSelectedRows([]);
    }

    fetchDataDebounced({
      index: 0,
      size: pageSize,
      sort: sortBy,
      search: searchFilter,
    });
  }, [fetchDataDebounced, pageSize, sortBy, searchFilter, filterValues]);

  const handleChangePage = useCallback(() => {
    fetchDataDebounced(
      {
        index: controlledPageIndex,
        size: pageSize,
        sort: sortBy,
        search: searchFilter,
      },
      {skipIncrement: controlledPageIndex < controlledPageCount - 1}
    );
    setChangingPage(false);
  }, [fetchDataDebounced, controlledPageIndex, pageSize, sortBy, searchFilter, controlledPageCount]);

  // ETODO PENDING LOADING REQS/FUTURE PASS: This does not work as intended, too many dependencies
  const updatePageCounter = useCallback((p) => {
    if (!p) {return;}

    const index = (isControlled ? controlledPageIndex : pageIndex) * pageSize;
    setPageCounter({
      start: index + 1,
      end: index + p.length,
    });
  }, [isControlled, controlledPageIndex, pageIndex, pageSize]);

  toggleRowSelectedInstance = useCallback((toggledRow, instanceSelectedRows, instancePage) => {
    toggleRowSelected(select, toggledRow, instanceSelectedRows, instancePage);
  }, [select]);

  const toggleRowSelectedExternal = useCallback((toggledRow) => {
    toggleRowSelected(select, toggledRow, selectedRows, page);
  }, [select, page, selectedRows]);

  const toggleAllPageRowsSelected = (checked, selRows, selRowIds, pg) => {
    if (!select || (![true, false].includes(checked))) {return;}

    if (select.max === 1) {
      if (checked === false) {
        setSelectedRow({});
      }

      return;
    }

    if (selRows === 'ALL') {
      setSelectedRows([]);
      return;
    }

    const selectablePageRows = select.rowDisabledFunc
      ? pg.map(row => row.original).filter(row => !select.rowDisabledFunc(row))
      : pg.map(row => row.original);

    if (checked === true) {
      setSelectedRows(
        [...selRows]
          .concat(selectablePageRows
            .filter(row => !selRowIds.includes(getRowID(row))))
      );
    } else if (checked === false) {
      const pageRowIds = pg.map(row => getRowID(row.original));
      setSelectedRows(selRows.filter(row => !pageRowIds.includes(getRowID(row))));
    }
  };

  const toggleAllRowsSelected = checked => {
    if (!select || (![true, false].includes(checked))) {return;}

    if (select.max === 1) {
      if (checked === false) {
        setSelectedRow({});
      }

      return;
    }

    if (checked === true) {
      setSelectedRows('ALL');
    } else if (checked === false) {
      setSelectedRows([]);
    }
  };

  toggleAllPageRowsSelectedInstance = useCallback((checked, instanceSelectedRows, instanceSelectedRowIds, instancePage) => {
    toggleAllPageRowsSelected(checked, instanceSelectedRows, instanceSelectedRowIds, instancePage);
  }, [getRowID]);

  const toggleAllPageRowsSelectedExternal = useCallback(checked => {
    toggleAllPageRowsSelected(checked, selectedRows, selectedRowIds, page);
  }, [getRowID, selectedRows, selectedRowIds, page]);

  /* Prepare Data Manager */
  if (dataManager) {
    dataManager.setData = newData => {
      setData(newData);
    };
    dataManager.toggleDisplayInfoPanel = toggleDisplayInfoPanel;
  }
  if (isControlled) {
    dataManager.refresh = () => {
      handleRefresh();
    };
    dataManager.toggleAllPageRowsSelected = toggleAllRowsSelected;
    dataManager.toggleAllCurrentPageRowsSelected = toggleAllPageRowsSelectedExternal;
  }

  /* Effects */

  useLayoutEffect(() => {
    if (selectedRows === 'ALL') {
      setSelectedRowIds('ALL');
    } else {
      setSelectedRowIds(selectedRows.map(row => getRowID(row)));
    }
  }, [selectedRows]);

  // Manually updating client-side data
  useLayoutEffect(() => {
    if (!isControlled) {
      setData(dataSet);
    }
  }, [isControlled, dataSet]);

  // Fetch data on refresh
  useLayoutEffect(() => {
    if (isControlled && !(subContent && subContent.rowID)) {
      handleRefresh();
    }
  }, [isControlled, handleRefresh]);

  // Fetch data on page change
  useLayoutEffect(() => {
    if (isControlled && changingPage) {
      handleChangePage();
    }
  }, [isControlled, handleChangePage, changingPage, controlledPageIndex]);

  // ETODO PENDING LOADING REQS/FUTURE PASS: missing dependency 'updatePageCounter', adding causes issues
  // Update page counter (e.g. 1-50)
  useLayoutEffect(() => {
    updatePageCounter(page);
  }, [page]);

  useLayoutEffect(() => {
    if (!isControlled) {
      resetScroll();
    }
  }, [isControlled, pageIndex]);

  // Expose selected row(s)
  useLayoutEffect(() => {
    if (dataManager && dataManager.setSelectedRows && select) {
      dataManager.setSelectedRows(select.max === 1 ? [selectedRow] : selectedRows);
    }
  }, [selectedRow, selectedRows]); // ETODO Verify that select is not needed here, having it here causing issues on generating totaled holdings report

  useLayoutEffect(() => {
    if (!select || !page) {return;}

    if (selectedRows === 'ALL') {
      setAllPageRowsSelected(true);
      return;
    }

    const selectablePageRows = select.rowDisabledFunc
      ? page.map(row => row.original).filter(row => !select.rowDisabledFunc(row))
      : page.map(row => row.original);

    setAllPageRowsSelected(selectablePageRows.some(row => selectedRowIds.includes(getRowID(row)))
    && selectablePageRows.some(row => !selectedRowIds.includes(getRowID(row)))
      ? null
      : (!!selectablePageRows.length && selectablePageRows.every(row => selectedRowIds.includes(getRowID(row)))));
  }, [page, selectedRows, selectedRowIds]);

  /* HMTL */

  // TODO REQS Filter Icon when active and inactive
  const filterIcon = <Icon svg={'filter'}
                           alternate={filterValues && Object.values(filterValues).some(filter => filter != null)}
                           width={'30px'}
                           height={'30px'}
                           margin={'5px 5px 5px 0'}
                           action={() => setDisplayFilters(!displayFilters)}/>;

  const searchInput = (
    <Search searchOptions={searches}
            searchFunc={(optionName, value) => setSearchFilter(getSearch(optionName, value))}
            defaultSearchOption={defaultSearch && searches.find(s => s.name
              === defaultSearch.option).title} // ETODO simplify if/when 1615 modified to not need title
            defaultSearchValue={defaultSearch && defaultSearch.value}/>
  );

  const paginationControls = <_PaginationControls>
    <Icon svg={'arrowCircle'}
          rotate={'180deg'}
          height={'20px'}
          width={'20px'}
          margin={'0 5px 0 0'}
          disabled={!canPreviousPage}
          action={() => gotoPreviousPage()}/>
    <_PageCount>{pageCounter.end === 0 ? 0 : `${pageCounter.start} - ${pageCounter.end}`}</_PageCount>
    <Icon svg={'arrowCircle'}
          height={'20px'}
          width={'20px'}
          margin={'0 0 0 5px'}
          disabled={!canNextPage}
          action={() => gotoNextPage()}/>
  </_PaginationControls>;
  // ETODO PENDING REQS
  // {rowsPerPageOptions && <div>
  //   <span>Select Page Size</span>
  //   <select value={pageSize} onChange={e => {setPageSize(Number(e.target.value));}}>
  //     {rowsPerPageOptions.map(option => (
  //       <option key={option} value={option}>
  //         Show {option}
  //       </option>
  //     ))}
  //   </select>
  // </div>
  // }

  const filtersPanel = !!filters && (
    <Panel title={'Filters'}
           content={Array.isArray(filters)
             ? filters
             : Object.entries(filters)
               .map(section => (
                 <>
                   <SectionTitle title={section[0]}/>
                   {!!section[1].description && <DescriptiveText alignment={'left'}>{section[1].description}</DescriptiveText>}
                   {section[1].filters}
                 </>
               ))
           }
           close={() => setDisplayFilters(false)}
           margin={'0 5px 0 0'}/>
  );

  const informationPanel = !!infoPanel && (
    <Panel {...infoPanel}
           close={() => setRightPanel(null)}
           margin={'0 0 0 5px'}/>
  );

  // TODO
  //  Table Selection Panel Styling?
  //  Different messaging if selecting all?
  //  Message and icon will change if selecting all and over limit?
  const selectionPanel = !!select && (
    <Panel title={'Selection'}
           showDividers={true}
           content={selectedRows === 'ALL'
             ? <DescriptiveText>All pages have been selected.</DescriptiveText>
             : (selectedRows.length && [
               selectedRows.length > select.max
               && <DescriptiveText>Please select no more then {select.max} items.</DescriptiveText>,
             ].concat(selectedRows.map(row => {
               const rowID = getRowID(row);
               // ETODO May need to consider possibility of columns[0].datapoint being a function. Don't use dataPoint?
               return <Row wrap={false} key={rowID} justifySubRow={'space between'}>
                 <DescriptiveText preset={'standard'} alignment={'left'}>
                   {row[primaryColumn || columns[0].dataPoint]}</DescriptiveText>
                 <Icon svg={'remove'} action={() => toggleRowSelectedExternal(row)}/>
               </Row>;
             }) || <DescriptiveText>No items selected.</DescriptiveText>).filter(i => !!i))}
           close={() => setRightPanel(null)}
           margin={'0 0 0 5px'}/>
  );

  const iconsKeyPanel = useMemo(() => {
    const byCategory = {};
    if (iconsKey) {
      iconsKey.forEach(icon => {
        icon.category = icon.category || null;
        byCategory[icon.category] = byCategory[icon.category] || [];
        byCategory[icon.category].push(icon);
      });
    }
    const categories = Object.keys(byCategory);

    // TODO PENDING REQS Table Icons Key Panel
    return <Panel
      title={'Icons Key'}
      content={categories.map(category => {
        const catKey = category || 'uncategorized'; // TODO below may need padding: '3px',
        return <Row key={catKey}>
          {categories.length > 1 && <SectionTitle title={category}/>}
          {byCategory[category].map(icon => (
            <Row key={[catKey, icon.title].join(':')}>
              <Column>
                <Row justifySubRow={'left'} wrap={false}>
                  <Icon svg={icon.image}
                        description={icon.title}
                        color={icon.color}
                        width={'20px'}
                        height={'20px'}
                        margin={'5px'}/>
                  <_IconTitle color={icon.color}>{icon.title}</_IconTitle>
                </Row>
                <DescriptiveText alignment={'left'}>
                  {typeof icon.description === 'function' ? icon.description() : icon.description}
                </DescriptiveText>
              </Column>
            </Row>
          ))}
        </Row>;
      })}
      close={() => setRightPanel(null)}
      margin={'0 0 0 5px'}/>;
  }, [iconsKey]);

  const toggleInfoPanel = useCallback(() => {
    if (!infoPanel.rowID) {
      infoPanel.setRow(page[0].original);
    }
    toggleDisplayInfoPanel();
  }, [infoPanel, toggleDisplayInfoPanel, page]);

  const panelIcons = useMemo(
    () => [
      infoPanel && {svg: 'details', action: toggleInfoPanel, disabled: !page[0]},
      select && (select.max !== 1) && {svg: 'selectionList', action: () => toggleDisplaySelectPanel()},
      (iconsKey && iconsKey.length)
      && (infoPanel || select && (select.max !== 1))
      && {svg: 'iconsKey', action: () => toggleDisplayIconsPanel()},
    ].filter(i => !!i),
    [infoPanel, select, iconsKey, toggleInfoPanel, toggleDisplayIconsPanel, toggleDisplaySelectPanel]
  );

  const panelSelector = !!panelIcons.length && <_PanelSelector>
    <Column justifySubColumn={'top'}
            alignItems={'center'}
            showDividers={true}>
      {panelIcons.map(iconProps => <Row key={iconProps.svg}><Icon margin={'0'}
                                                                  width={'30px'}
                                                                  height={'30px'} {...iconProps}/></Row>)}
    </Column>
  </_PanelSelector>;

  const renderTableHeaders = (options) => {
    const opts = options || {};
    const {isSubContent} = opts;

    return <thead>{headerGroups.map(headerGroup => {
      const {key: headerKey, ...restHeaderProps} = headerGroup.getHeaderGroupProps();
      const headers = isSubContent ? headerGroup.headers.filter(column => !column.hideInSubContent) : headerGroup.headers;

      return (
        <tr key={headerKey} {...restHeaderProps}>
          {headers.map(column => {
            const {key: colKey, ...restColProps} = column.getHeaderProps(column.getSortByToggleProps());
            return <_TableHeader key={colKey} column={column} {...restColProps}>
              <Row justifySubRow={column.alignment || 'left'} alignItems={'middle'} alignSubRow={'middle'}>
                {column.render('Header')}
                {!isSubContent && column.isSorted && <Icon svg={column.isSortedDesc ? 'arrowCircle' : 'arrowCircle'}
                                                           rotate={column.isSortedDesc ? '90deg' : '-90deg'}
                                                           margin={'0 0 0 5px'}
                                                           height={'fill'}
                                                           width={'1em'}/>}
              </Row>
            </_TableHeader>;
          })}
        </tr>
      );
    })}
    </thead>;
  };

  let table;

  if (subContent && subContent.rowID) {
    const drilldownRow = rows[0];
    prepareRow(drilldownRow);
    const {...drilldownProps} = drilldownRow.getRowProps();

    table = (
      <>
        <Button importance={'secondary'}
                size={'md'}
                label={subContent.backButtonLabel}
                width={'fit-content'}
                action={() => subContent.setRow(null)}
        />

        {/*Table Body Row*/}
        <Row wrap={false}
             justifySubRow={'center'}
             alignSubRow={'stretch'}
             alignItems={'stretch'}
             layout={{grow: 1}}>
          <_TableWrapper>
            <_TableFixHead>
              <_Table {...getTableProps()} pointerEvents={'none'}>
                <tbody {...getTableBodyProps()}>
                  <_TableRow {...drilldownProps}>
                    {drilldownRow.cells.filter(cell => !cell.column.hideInSubContent).map(cell => {
                      const {
                        key: cellKey,
                        ...restCellProps
                      } = cell.getCellProps();
                      return <_TableCell key={cellKey} {...restCellProps}
                                         column={cell.column}
                                         onClick={null}>
                        {cell.render('Cell')}
                      </_TableCell>;
                    })}
                  </_TableRow>
                </tbody>
                {renderTableHeaders({isSubContent: true})}
              </_Table>
            </_TableFixHead>
            {loading && <_Overlay><LoadingSpinner/></_Overlay>}
          </_TableWrapper>
          {/*{rightPanel === 'info' && informationPanel}*/}
          {/*{rightPanel === 'select' && selectionPanel}*/}
          {/*{rightPanel === 'icons' && iconsKeyPanel}*/}
          {/*{panelSelector}*/}
        </Row>

        {subContent.content}
      </>
    );
  } else {
    const selectedPageRows = getRowID ? page.filter(row => selectedRowIds.includes(getRowID(row.original))) : page;

    table = (
      <>
        {/*ETODO DEBUG: selection (for badges)*/}
        {/*<div>*/}
        {/*  <div>Selected Count: {Array.isArray(selectedRows) ? selectedRows.length : 'N/A'}</div>*/}
        {/*  <div>Over Limit: {Array.isArray(selectedRows) ? (selectedRows.length > select.max ? 'Yes' : 'No') : 'N/A'}</div>*/}
        {/*</div>*/}

        {/*Table Header Row*/}
        <Row height={'fit-content'}
             wrap={false}
             justifySubRow={'space between'}
             alignItems={'middle'}>
          <Row width={'fit-content'}
               wrap={false}
               justifySubRow={'left'}
               alignItems={'middle'}>
            {!!(filters && Object.keys(filters).length) && filterIcon}
            {!!(searches && searches.length) && searchInput}
          </Row>
          <Row width={'fit-content'} wrap={false}>
            {!!(iconsKey && iconsKey.length)
            && (!infoPanel && !(select && (select.max !== 1)))
            && <Icon margin={'0'} svg={'iconsKey'} width={'30px'} height={'30px'} action={() => toggleDisplayIconsPanel()}/>}
            {paginationControls}
          </Row>
        </Row>

        {/*Select All Message Row*/}
        {!!allPageRowsSelected
        && <Row height={'fit-content'}
                minHeight={'40px'}
                wrap={true}
                justifySubRow={'center'}
                alignSubRow={'middle'}
                alignItems={'middle'}
                margin={'0 0 5px 0'}
                backgroundColor={props.theme.dark ? 'rgba(255,255,255, 0.05)' : 'rgba(0,0,0, 0.05)'}
                borderRadius={'3px'}>
          <_SelectAllMessage>
            {selectedRows === 'ALL'
              ? 'All pages have been selected.'
              : `${selectedPageRows.length} rows on this page have been added to your selection.`}
          </_SelectAllMessage>
          {(select.type === 'all' && selectedRows !== 'ALL')
          && <Button importance={'secondary'}
                     size={'sm'}
                     label={'Select All Pages'}
                     width={'fit-content'}
                     action={() => setSelectedRows('ALL')}/>}
        </Row>}

        {/*Table Body Row*/}
        <Row wrap={false}
             justifySubRow={'center'}
             alignSubRow={'stretch'}
             alignItems={'stretch'}
             layout={{grow: 1}}
             minHeight={'0'}>
          {!!displayFilters && filtersPanel}
          <_TableWrapper>
            <_TableFixHead ref={tableRef}>
              <_Table {...getTableProps()}>
                <tbody {...getTableBodyProps()}>
                  {!!error && <_TableRow>
                    <_TableCell column={{minWidth: '100px', alignment: 'center'}} colSpan={columnDefs.length + (select ? 1 : 0)}>
                      There was an issue loading the data. Please try refreshing. If the issue persists please contact Client
                      Services at {session?.profile?.brand?.advisorCSEmail} or
                      {` ${formatter('phone')(session?.profile?.brand?.advisorCSPhone)}`}.
                    </_TableCell>
                  </_TableRow>}
                  {!error && !page.length && loading === false
                  && <_TableRow>
                    <_TableCell column={{minWidth: '100px', alignment: 'center'}} colSpan={columnDefs.length + (select ? 1 : 0)}>
                      {(filterValues && Object.values(filterValues).some(filter => filter != null)
                        || (searchFilter && Object.values(searchFilter).some(filter => filter != null)))
                        ? noFilteredDataMsg
                        : noDataMsg}</_TableCell>
                  </_TableRow>}
                  {!error && page.map(row => {
                    prepareRow(row);
                    row.onClickDisabled = rowOnClickDisabled && rowOnClickDisabled(row.original);
                    const {key: rowKey, ...restRowProps} = row.getRowProps();
                    return <_TableRow key={rowKey}
                                      {...restRowProps}
                                      hasClickAction={!!rowOnClick}
                                      disabled={row.onClickDisabled}
                                      isActive={infoPanel && infoPanel.rowID === row.id}>
                      {row.cells.map(cell => {
                        const {key: cellKey, ...restCellProps} = cell.getCellProps();
                        return (
                          <_TableCell key={cellKey}
                                      {...restCellProps}
                                      column={cell.column}
                                      disabled={row.onClickDisabled}
                                      onClick={() => handleCellClick(cell)}>
                            {cell.render('Cell')}
                          </_TableCell>
                        );
                      })}
                    </_TableRow>;
                  })}
                </tbody>
                {renderTableHeaders({isSubContent: false})}
              </_Table>
            </_TableFixHead>
            {loading && <_Overlay><LoadingSpinner/></_Overlay>}
          </_TableWrapper>
          {rightPanel === 'info' && informationPanel}
          {rightPanel === 'select' && selectionPanel}
          {rightPanel === 'icons' && iconsKeyPanel}
          {panelSelector}
        </Row>
        {/*ETODO DEBUG: pagination*/}
        {/*<pre>*/}
        {/*  <code>*/}
        {/*    {JSON.stringify(*/}
        {/*      {*/}
        {/*        pageIndex,*/}
        {/*        pageSize,*/}
        {/*        controlledPageCount,*/}
        {/*        canNextPage,*/}
        {/*        canPreviousPage,*/}
        {/*      },*/}
        {/*      null,*/}
        {/*      2*/}
        {/*    )}*/}
        {/*  </code>*/}
        {/*</pre>*/}
      </>
    );
  }

  return <_Wrapper maxHeight={maxHeight} height={!(subContent && subContent.rowID) ? height : null}>
    {table}
  </_Wrapper>;
};
const mapStateToProps = state => ({
  session: state.session,
});

export default withTheme(connect(mapStateToProps, null)(Table));

// ETODO PENDING REQS validate page size is among page size options? Jeremy
// ETODO ignoring Parameter <parameter> is not described in JSDoc for now
Table.propTypes = {
  session: PropTypes.object,
  /**
   * Rows to use for "client-side" tables without searching and filtering.
   * Can only be passed if not passing a dataManager with a gatherData function, in which case it is required.
   */
  dataSet: PropTypes.arrayOf(PropTypes.object),
  /**
   * Table data management object. Effectively required if creating a table that supports searching and filtering, or selecting.
   * Some properties are conditionally added to this by the Table, for the purposes of exposing data/functionality to the parent.
   */
  dataManager: PropTypes.shape({
    /**
     * Function to gather data for tables that require searching and filtering.
     * Can only be passed if not passing a dataSet, in which case it is required.
     * param {Object} params Search, limit, offset, and sort parameters.
     * return {Promise.<Object[]>} The gathered data rows.
     */
    gatherData: (props, propName, componentName) => {
      PropTypes.checkPropTypes({[propName]: PropTypes.func}, props, propName, componentName);
      if ((props[propName] && props.dataSet) || (!props[propName] && !props.dataSet)) {
        return new Error(`Either a ${propName} function or a dataSet (but not both) is required for a ${componentName}`);
      }
      return null;
    },
    /**
     * Function called when rows are selected.
     * Required if supporting selection, so that selected rows may be exposed to parent.
     * param {Object[]} selectedRows The currently selected rows.
     */
    setSelectedRows: (props, propName, componentName) => {
      PropTypes.checkPropTypes({[propName]: PropTypes.func}, props, propName, componentName);
      if (props.select && !props[propName]) {
        return new Error(`A ${propName} function is required for a ${componentName} that supports selection.`);
      }
      return null;
    },
    /**
     * (Added by Table) Function to toggle all selected rows, pass in false to clear selection.
     * Only available for tables with select.
     */
    toggleAllPageRowsSelected: PropTypes.func,
    /**
     * (Added by Table) Function to toggle all selected rows on the current page, pass in false to clear selection.
     * Only available for tables with select.
     */
    toggleAllCurrentPageRowsSelected: PropTypes.func,
    /**
     * (Added by Table) Function to refresh to first page, with current search and filters.
     * Only available for tables with a gatherData function.
     */
    refresh: PropTypes.func,
    /**
     * (Added by Table) Function to manually update the currently loaded rows.
     * Only available for tables with a gatherData function.
     * @param {Object[]} newData The array of rows to update to.
     */
    setData: PropTypes.func,
    /**
     * (Added by Table) Function to manually toggle display of the Info Panel.
     */
    toggleDisplayInfoPanel: PropTypes.func,
  }),
  /**
   * Defines the table's columns.
   */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Column title.
       */
      title: PropTypes.string.isRequired,
      /**
       * The column data point. Corresponds to the react-table column option "accessor".
       * If a string, this is the key of the row data used to lookup the cell value, supporting deeply nested values.
       * If a function, this receives the original row data and returns the cell value.
       */
      dataPoint: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      /**
       * JSX (or function that returns it) to put in the column. Corresponds to the react-table column option "Cell".
       * Receives the table instance and cell model as arguments.
       */
      render: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
      /**
       * The column type. Different types may assign default alignment and formatters to the column.
       */
      type: PropTypes.oneOf([
        'text',
        'number',
        'phone',
        'percent',
        'percentDecimal',
        'currency',
        'date',
        'datetime',
        'custom',
        'object',
      ]).isRequired,
      /**
       * Number of decimal places for percent and percentDecimal.
       */
      decimalPlaces: PropTypes.number,
      /**
       * Column alignment (as CSS text-align value). Takes precedence over default alignment from column type.
       */
      alignment: PropTypes.oneOf(['left', 'right', 'center']),
      /**
       * Minimum column width (px).
       */
      minWidth: PropTypes.number.isRequired,
      /**
       * Maximum column width (px).
       */
      maxWidth: PropTypes.number,
      /**
       * Whether the table can be sorted by the column.
       */
      isSortable: PropTypes.bool.isRequired,
      /**
       * Function to perform when a row is clicked. Receives the original row data and the column as arguments.
       * @param {Object=} row Row data
       * @param {Object=} column React-table column metadata
       */
      onClick: PropTypes.func,
      /**
       * If true, the column will not be included in the displayed row from Main Table within Sub-Content areas.
       */
      hideInSubContent: PropTypes.bool,
      /**
       * Vertical align content by center
       */
      verticalAlign: PropTypes.bool,
      /**
       * If true, apply word-break and hyphens styling to all column text.
       */
      wrapBreakWords: PropTypes.bool,
    })
  ).isRequired,
  /**
   * Defines table row selection behavior. If not passed, the table will not support row selection.
   */
  select: PropTypes.shape({
    /**
     * Allows either all of the current page's rows, or all table rows to be selected via a header checkbox.
     */
    type: PropTypes.oneOf(['page', 'all']),
    /**
     * The maximum number of rows that can be selected.
     */
    max: PropTypes.number.isRequired,
    /**
     * Rows (with complete data) that should be pre-selected on initial page load (regardless of page).
     */
    preselectedRows: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.string]),
    /**
     * Function to determine if selection should be disabled for a row. Receives the original row data as an argument.
     * @param {Object=} row Row data
     * @return {Boolean}
     */
    rowDisabledFunc: PropTypes.func,
  }),
  /**
   * The means of uniquely identifying a row. Corresponds to the react-table table option "getRowId".
   * If a string, this is the key of the row data (e.g. "id").
   * If a function, this receives the original row data and the relative index, and unique identifier.
   * Required if the table supports selection, info panels, or sub-content.
   */
  rowIdentifier: (props, propName, componentName) => {
    PropTypes.checkPropTypes({[propName]: PropTypes.oneOfType([PropTypes.string, PropTypes.func])}, props, propName, componentName);
    if ((props.select || props.infoPanel || props.subContent) && !props[propName]) {
      return new Error(`A ${propName} is required for a ${componentName} that supports selection, info panels, or sub-content.`);
    }
    return null;
  },
  /**
   * Specifies the default column to sort by, and its direction.
   */
  defaultSort: PropTypes.shape({
    /**
     * The default column (identified by dataPoint) to sort by.
     */
    column: PropTypes.string.isRequired,
    /**
     * Whether the specified column's sort direction should default to descending (rather than ascending).
     */
    desc: PropTypes.bool.isRequired,
  }),
  /**
   * Function to perform when a row is clicked. Receives the original row data and the column as arguments.
   * @param {Object=} row Row data
   * @param {Object=} column React-table column metadata
   */
  rowOnClick: PropTypes.func,
  /**
   * Function to determine if a row's On Row Click Action should be disabled. Does not affect row selection.
   * @param {Object=} row Row data
   * @return {Boolean}
   */
  rowOnClickDisabled: PropTypes.func,
  /**
   * The number of rows to display per page. Defaults to MAX\_PAGINATED\_ITEMS in configuration.
   */
  rowsPerPage: PropTypes.number,
  /**
   * The available choices for the number of rows to display per page.
   */
  // rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  /**
   * Height of the overall component, as CSS (e.g. '500px', '50%').
   */
  height: PropTypes.string,
  /**
   * Max Height of the overall component, as CSS (e.g. '500px', '50%'). Defaults to 85vh
   */
  maxHeight: PropTypes.string,
  /**
   * Search options available for the table. Order specified is order displayed.
   */
  searches: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Search title.
       */
      title: PropTypes.string.isRequired,
      /**
       * Search field name.
       */
      name: PropTypes.string.isRequired,
      /**
       * Whether the search should be fuzzy. Only applicable to searches that will be performed via MySQL.
       */
      isFuzzy: PropTypes.bool.isRequired,
    })
  ),
  /**
   * Specifies the default search to initialize the table with. Corresponds to the searches prop.
   */
  defaultSearch: PropTypes.shape({
    /**
     * Search option name.
     */
    option: PropTypes.string.isRequired,
    /**
     * Raw search value.
     */
    value: PropTypes.bool.isRequired,
  }),
  /**
   * Defines table filters, which can be optionally be grouped into sections if passed as an object.
   */
  filters: PropTypes.oneOfType([
    /**
     * JSX of components to use as filters. Order specified is order displayed.
     */
    PropTypes.arrayOf(PropTypes.element),
    /**
     * Object representing filter sections. Keys are the section titles.
     */
    PropTypes.objectOf(
      PropTypes.shape({
        /**
         * Description to display under the section title.
         */
        description: PropTypes.string,
        /**
         * JSX of components to use as filters. Order specified is order displayed.
         */
        filters: PropTypes.arrayOf(PropTypes.element).isRequired,
      })
    ),
  ]),
  /**
   * An object with filter names as keys and their current values as values - this should be part of parent state.
   * If using hooks, use the usual setVariable({ //new object }) pattern.
   * If using setState or state management, do setState({...state, {...filters}}) to ensure a new object is created for filterValues.
   * Required if the table supports selection and filters.
   */
  filterValues: (props, propName, componentName) => {
    PropTypes.checkPropTypes({[propName]: PropTypes.object}, props, propName, componentName);
    if (props.filters && !props[propName]) {
      return new Error(`A ${propName} is required for a ${componentName} that supports filters.`);
    }
    return null;
  },
  /**
   * The index of the column to apply the icons key to, defaults to 0 for first column.
   */
  iconsKeyIdx: PropTypes.number,
  /**
   * Describes icons that will populate the table Icons Key.
   */
  iconsKey: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Icon svg name (or link)
       */
      image: PropTypes.string.isRequired,
      /**
       * Icon title
       */
      title: PropTypes.string.isRequired,
      /**
       * Color associated with the icon
       */
      color: PropTypes.string.isRequired,
      /**
       * Icon description. If a function, receives the row and table instance when being rendered in a row via display condition.
       */
      description: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      /**
       * Icon category name (if applicable)
       */
      category: PropTypes.string,
      /**
       * If a row meets this condition, the icon is displayed in the icon column
       */
      displayCondition: PropTypes.func,
      /**
       * Action to perform when the icon is clicked in the icon column, given the row
       */
      action: PropTypes.func,
    })
  ),
  /**
   * The column (identified by dataPoint) to use for icons and the selection panel. Defaults to first non-select column.
   */
  primaryColumn: PropTypes.string,
  /**
   * Defines an Info Panel to use for the table.
   */
  infoPanel: PropTypes.shape({
    /**
     * Info Panel title.
     */
    title: PropTypes.string.isRequired,
    /**
     * Info Panel JSX content (or a function that returns it).
     */
    content: PropTypes.node.isRequired,
    /**
     * The current row's unique identifier (as defined by the row identifier prop).
     */
    rowID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * Function to set the Info Panel row on the parent.
     */
    setRow: PropTypes.func.isRequired,
  }),
  /**
   * The content to display (drilldown) when a row or column is clicked (can be any component, another table, or any JSX).
   * Drilldown happens in addition to any row or column click effects.
   */
  subContent: PropTypes.shape({
    /**
     * Label for back button in drilldown.
     */
    backButtonLabel: PropTypes.string.isRequired,
    /**
     * Drilldown JSX content (or a function that returns it).
     */
    content: PropTypes.node.isRequired,
    /**
     * The current row's unique identifier (as defined by the row identifier prop).
     * Set to any falsey value (including 0) to not display subcontent.
     */
    rowID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * Function to set the subContent row on the parent.
     */
    setRow: PropTypes.func.isRequired,
  }),
  /**
   * Theme (usually added by ThemeProvider).
   */
  theme: PropTypes.object,
  noData: PropTypes.shape({
    /**
     * What to display for table when there is no data and no filters applied. Defaults to "No data could be found.",
     * or "No results found matching search criteria." if a filter is currently applied.
     */
    standard: PropTypes.node,
    /**
     * What to display for table when there is no data and there are filters/search applied. Defaults to "No results found matching search criteria."
     */
    filtered: PropTypes.node,
  }),
};
